import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BADGES } from '@/data/rewardsData';
import { Trophy, Sun, Lock } from 'lucide-react';

const catIcons = {
  achievement: Trophy,
  seasonal: Sun,
  secret: Lock,
};

const catColors = {
  achievement: 'hsl(var(--gold))',
  seasonal: 'hsl(var(--tier-bloom))',
  secret: 'hsl(var(--muted-foreground))',
};

const catLabels = {
  achievement: 'Achievement',
  seasonal: 'Seasonal',
  secret: 'Secret',
};

export const BadgesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? BADGES : BADGES.filter(b => b.cat === filter);

  return (
    <section id="badges" ref={ref} className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 0%, hsl(var(--card) / 0.2) 50%, transparent 100%)' }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Royal ornamental divider */}
        <div className="flex items-center gap-3 justify-center mb-10">
          <div className="h-px w-16 bg-gold/20" />
          <svg width="16" height="16" viewBox="0 0 20 20" className="text-gold/40">
            <path d="M10 0L12 8L20 10L12 12L10 20L8 12L0 10L8 8Z" fill="currentColor" />
          </svg>
          <div className="h-px w-16 bg-gold/20" />
        </div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 lg:mb-20"
        >
          <span className="font-body text-xs tracking-[0.3em] uppercase text-gold mb-4 block">Achievements</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-foreground font-medium leading-tight">
            Collect <span className="text-shimmer-gold italic">Badges</span>
          </h2>
          <p className="font-body text-base text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed">
            Unlock badges through milestones, seasonal events, and hidden achievements. Each badge earns you bonus Glow Points.
          </p>
        </motion.div>

        {/* Filter */}
        <div className="flex justify-center gap-2 mb-10 flex-wrap">
          {['all', 'achievement', 'seasonal', 'secret'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`font-body text-sm px-4 py-2 rounded-full transition-all duration-300 border ${
                filter === f
                  ? 'bg-gold/15 text-gold border-gold/20'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:border-border/50'
              }`}
            >
              {f === 'all' ? 'All Badges' : catLabels[f]}
            </button>
          ))}
        </div>

        {/* Badges Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-4">
          {filtered.map((badge, i) => {
            const Icon = catIcons[badge.cat];
            return (
              <motion.div
                key={badge.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Card className="bg-gradient-card border-border/40 rounded-xl p-5 text-center hover:border-gold/15 transition-all duration-500 group h-full flex flex-col items-center">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-110"
                    style={{ background: `${catColors[badge.cat]}15` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: catColors[badge.cat] }} />
                  </div>
                  <h4 className="font-display text-sm text-foreground mb-1">{badge.name}</h4>
                  <p className="font-body text-xs text-muted-foreground leading-relaxed mb-3 flex-1">{badge.desc}</p>
                  <Badge
                    className="font-body text-[10px] px-2 py-0.5 rounded-full border-0 mt-auto"
                    style={{ background: `${catColors[badge.cat]}15`, color: catColors[badge.cat] }}
                  >
                    +{badge.gp} GP
                  </Badge>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
